import { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table";
import { ArrowLeft, Download, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ContentContainer, PageLayout, PageTitle } from "@/components/layout";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  useGetPriceRemarkPendingExpiredQuery,
  useGetPriceRemarkPendingItemsQuery,
  useGetPriceRemarkPendingStoreQuery,
} from "@/services/price-remark/pending";
import { PendingItem, PendingItemStatus } from "@/services/price-remark/types";
import { formatNumber, formatToBRL } from "@/utils/formatToMoney";

import { KpiCards } from "../../components/kpi-cards";
import { QueryErrorAlert } from "../../components/query-error-alert";
import { ColumnHeader } from "../../components/table/column-header";
import { MonitoringTable } from "../../components/table/monitoring-table";
import { ServerPagination } from "../../components/table/server-pagination";
import { exportRowsToXlsx } from "../../utils/export-xlsx";
import { extractQueryErrorMessage } from "../../utils/query-error";
import { BatchCompositionChart } from "../components/batch-composition-chart";
import { BatchesTable } from "../components/batches-table";
import { PendingFilter } from "../components/pending-filter";
import { ItemSituationBadge } from "../components/situation-badge";
import { ITEM_SITUATIONS } from "../components/situation-styles";
import { WindowBar } from "../components/window-bar";
import { usePendingFilterActions } from "../ducks/filter/hooks/actions";
import { usePendingFilterState } from "../ducks/filter/hooks/filterState";
import { useExportWorkOrder } from "../hooks/use-pending-exports";
import { useStableScrollbar } from "../hooks/use-stable-scrollbar";

// As duas colunas compostas ficam com enableSorting: false porque o clique
// mandaria um sortBy fora da whitelist PENDING_SORT_COLUMNS, que a API rejeita.
const columns: ColumnDef<PendingItem>[] = [
  {
    accessorKey: "lote",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Lote" align="center" />
    ),
    meta: { label: "Lote", cellClassName: "text-center" },
    cell: ({ row }) => row.original.batch.split("-").reverse().join("/"),
  },
  {
    accessorKey: "sku",
    header: ({ column }) => (
      <ColumnHeader column={column} title="SKU" align="center" />
    ),
    meta: { label: "SKU", cellClassName: "text-center" },
  },
  {
    accessorKey: "descricao",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Descrição" align="center" />
    ),
    meta: { label: "Descrição", cellClassName: "text-center" },
    cell: ({ row }) => (
      <span
        className="mx-auto block max-w-[300px] truncate"
        title={row.original.description ?? undefined}
      >
        {row.original.description ?? "—"}
      </span>
    ),
  },
  {
    accessorKey: "setor",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Setor" align="center" />
    ),
    meta: { label: "Setor", cellClassName: "text-center text-gray-500" },
    cell: ({ row }) => row.original.sector ?? "—",
  },
  {
    accessorKey: "tipo",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Tipo" align="center" />
    ),
    meta: { label: "Tipo", cellClassName: "text-center" },
  },
  {
    accessorKey: "preco_de",
    header: ({ column }) => (
      <ColumnHeader column={column} title="De" align="center" />
    ),
    meta: { label: "De", cellClassName: "text-center text-gray-500" },
    cell: ({ row }) =>
      row.original.priceFrom == null ? "—" : formatToBRL(row.original.priceFrom),
  },
  {
    accessorKey: "preco_por",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Por" align="center" />
    ),
    meta: { label: "Por", cellClassName: "text-center font-semibold" },
    cell: ({ row }) => formatToBRL(row.original.priceTo),
  },
  {
    accessorKey: "variacao",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Δ%" align="center" />
    ),
    meta: { label: "Δ%", cellClassName: "text-center" },
    cell: ({ row }) =>
      row.original.variation == null ? (
        "—"
      ) : (
        <span
          className={
            row.original.variation < 0 ? "text-green-600" : "text-red-600"
          }
        >
          {row.original.variation}%
        </span>
      ),
  },
  {
    accessorKey: "estoque",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Estoque" align="center" />
    ),
    meta: { label: "Estoque", cellClassName: "text-center" },
    cell: ({ row }) => formatNumber(row.original.stock),
  },
  {
    id: "etiquetas",
    header: () => <div className="text-center">Etiquetas</div>,
    enableSorting: false,
    meta: { label: "Etiquetas", cellClassName: "text-center font-mono text-xs" },
    cell: ({ row }) => `${row.original.issued} / ${row.original.target}`,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <ColumnHeader column={column} title="Situação" align="center" />
    ),
    meta: { label: "Situação", cellClassName: "text-center" },
    cell: ({ row }) => <ItemSituationBadge status={row.original.status} />,
  },
  {
    id: "observacao",
    header: () => <div className="text-center">Observação</div>,
    enableSorting: false,
    meta: { label: "Observação", cellClassName: "text-center text-gray-500" },
    cell: ({ row }) => {
      const { justification, justificationContext, lastIssuedAt, operator } =
        row.original;
      if (justification) {
        return `${justificationContext ?? ""} · ${justification}`.trim();
      }
      if (lastIssuedAt) {
        return `${lastIssuedAt}${operator ? ` · ${operator}` : ""}`;
      }
      return "—";
    },
  },
];

export function PriceRemarkPendingStore() {
  useStableScrollbar();

  const navigate = useNavigate();
  const { codigo } = useParams();
  const storeCode = Number(codigo);

  const { filters } = usePendingFilterState();
  const { updateFilter } = usePendingFilterActions();
  const { exportAll, isExporting } = useExportWorkOrder();

  const isValidStore = Number.isInteger(storeCode) && storeCode > 0;

  const {
    data: store,
    isFetching: isFetchingStore,
    isError: isStoreError,
    error: storeError,
    refetch: refetchStore,
  } = useGetPriceRemarkPendingStoreQuery(
    { storeCode, labelType: filters.labelType },
    { skip: !isValidStore },
  );

  const storeDetail = store?.store ?? null;

  const selectedBatch = useMemo(
    () => storeDetail?.batches.find((batch) => batch.batch === filters.batch) ?? null,
    [storeDetail, filters.batch],
  );

  // O chip mostra a contagem do recorte, senão um lote só com item sem estoque
  // devolveria tabela vazia e pareceria falha de dado.
  const chips = useMemo(() => {
    const escopo = selectedBatch ?? storeDetail;
    const porSituacao: Record<PendingItemStatus, number> = {
      NAO_INICIADO: escopo?.notStarted ?? 0,
      PARCIAL: escopo?.partial ?? 0,
      FALHA_IMPRESSAO: escopo?.printFailure ?? 0,
      JUSTIFICADO: escopo?.justified ?? 0,
      SEM_ESTOQUE: escopo?.outOfStock ?? 0,
      CONCLUIDO: escopo?.completed ?? 0,
    };

    return [
      ...(Object.keys(ITEM_SITUATIONS) as PendingItemStatus[]).map((status) => ({
        value: status as string | null,
        label: ITEM_SITUATIONS[status].label,
        count: porSituacao[status],
      })),
      { value: null, label: "Todos", count: escopo?.items ?? 0 },
    ];
  }, [storeDetail, selectedBatch]);

  const escopoItens = (selectedBatch ?? storeDetail)?.items ?? 0;

  // Quando a situação escolhida está zerada no recorte, a seleção cai para
  // "Todos" — seleção derivada, não despachada, para preservar o foco original.
  const effectiveStatus = useMemo(() => {
    if (!filters.status || !storeDetail) return filters.status;
    const atual = chips.find((chip) => chip.value === filters.status);
    return atual && atual.count > 0 ? filters.status : null;
  }, [chips, storeDetail, filters.status]);

  const {
    data: items,
    isFetching: isFetchingItems,
    isError: isItemsError,
    error: itemsError,
    refetch: refetchItems,
  } = useGetPriceRemarkPendingItemsQuery(
    {
      storeCode,
      labelType: filters.labelType,
      batch: filters.batch,
      status: effectiveStatus,
      page: filters.page,
      perPage: filters.perPage,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    },
    { skip: !isValidStore },
  );

  const { data: expired, isFetching: isLoadingExpired } =
    useGetPriceRemarkPendingExpiredQuery(
      { storeCode, labelType: filters.labelType },
      { skip: !isValidStore },
    );

  const rows = useMemo(() => items?.data ?? [], [items]);
  const total = items?.total ?? 0;

  const sorting = useMemo<SortingState>(
    () =>
      filters.sortBy
        ? [{ id: filters.sortBy, desc: filters.sortOrder !== "asc" }]
        : [],
    [filters.sortBy, filters.sortOrder],
  );

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    const first = next[0];
    updateFilter({ key: "sortBy", value: first?.id ?? null });
    updateFilter({
      key: "sortOrder",
      value: first ? (first.desc ? "desc" : "asc") : null,
    });
    updateFilter({ key: "page", value: 1 });
  };

  const kpis = useMemo(
    () => [
      {
        label: "Itens a remarcar",
        value: formatNumber(storeDetail?.items ?? 0),
        hint: `${formatNumber(storeDetail?.inStock ?? 0)} com estoque`,
      },
      {
        label: "Não iniciados",
        value: formatNumber(storeDetail?.notStarted ?? 0),
        tone: "danger" as const,
        hint: "ninguém tocou",
      },
      {
        label: "Parciais",
        value: formatNumber(storeDetail?.partial ?? 0),
        tone: "warning" as const,
        hint: "começou e parou",
      },
      {
        label: "Justificados",
        value: formatNumber(storeDetail?.justified ?? 0),
        hint: "exceção registrada",
      },
      {
        label: "Falha de impressão",
        value: formatNumber(storeDetail?.printFailure ?? 0),
        tone: "danger" as const,
        hint: "ação técnica",
      },
      {
        label: "Cobertura de itens",
        value: storeDetail?.coverage == null ? "—" : `${storeDetail.coverage} %`,
        tone: "positive" as const,
        hint: "com etiqueta ÷ com estoque",
      },
    ],
    [storeDetail],
  );

  const emptyMessage = useMemo(() => {
    if (escopoItens === 0) return "Nenhum item a remarcar neste recorte.";

    const rotulo = effectiveStatus
      ? ITEM_SITUATIONS[effectiveStatus as PendingItemStatus]?.label
      : null;
    if (!rotulo) return "Nenhum item a remarcar neste recorte.";

    const onde = filters.batch
      ? `no lote ${filters.batch.split("-").reverse().join("/")}`
      : "nesta loja";
    return `Nenhum item em "${rotulo}" ${onde} — os ${formatNumber(
      escopoItens,
    )} itens estão em outras situações.`;
  }, [escopoItens, effectiveStatus, filters.batch]);

  const titulo = storeDetail
    ? `Loja ${storeDetail.storeCode} — ${storeDetail.city ?? storeDetail.storeName ?? ""}${
        storeDetail.state ? `/${storeDetail.state}` : ""
      }`
    : `Loja ${codigo}`;

  if (!isValidStore) {
    return (
      <PageLayout>
        <ContentContainer>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
            Loja inválida.
          </div>
        </ContentContainer>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageTitle
        title={titulo}
        description="A conta exata desta loja: o que falta remarcar, por lote e por item."
      />
      <ContentContainer>
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 hover:text-gray-900"
                    onClick={() => navigate("..")}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Pendências da rede
                  </button>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Loja {codigo}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <WindowBar
            window={store?.window}
            durationMs={store?.durationMs}
            cache={store?.cache}
            expired={expired}
            isLoadingExpired={isLoadingExpired}
            storeScope
          />

          <div className="flex flex-wrap items-end justify-between gap-3">
            <PendingFilter />
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={isFetchingStore}
              onClick={() => {
                refetchStore();
                refetchItems();
              }}
            >
              <RefreshCw className="mr-1 h-4 w-4" />
              Recalcular
            </Button>
          </div>

          {isStoreError ? (
            <QueryErrorAlert
              message={extractQueryErrorMessage(storeError)}
              onRetry={refetchStore}
            />
          ) : (
            <>
              <KpiCards items={kpis} />

              {storeDetail && storeDetail.batches.length > 0 && (
                <BatchCompositionChart batches={storeDetail.batches} />
              )}

              <Card className="overflow-hidden">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b bg-muted/30 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Lotes</p>
                    <p className="text-xs text-gray-500">
                      clique num lote para filtrar os itens abaixo
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {filters.batch && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => {
                          updateFilter({ key: "batch", value: null });
                          updateFilter({ key: "page", value: 1 });
                        }}
                      >
                        Ver todos os lotes
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      disabled={(storeDetail?.batches.length ?? 0) === 0}
                      onClick={() =>
                        exportRowsToXlsx(
                          (storeDetail?.batches ??
                            []) as unknown as Record<string, unknown>[],
                          `lotes-pendentes-loja-${storeCode}`,
                        )
                      }
                    >
                      <Download className="mr-1 h-4 w-4" />
                      Exportar lotes
                    </Button>
                  </div>
                </div>
                <BatchesTable
                  batches={storeDetail?.batches ?? []}
                  isLoading={isFetchingStore}
                  selected={filters.batch}
                  onSelect={(lote) => {
                    updateFilter({
                      key: "batch",
                      value: filters.batch === lote ? null : lote,
                    });
                    updateFilter({ key: "page", value: 1 });
                  }}
                />
              </Card>

              <Card className="overflow-hidden">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b bg-muted/30 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {filters.batch
                        ? `Itens do lote ${filters.batch
                            .split("-")
                            .reverse()
                            .join("/")}`
                        : "Itens de todos os lotes da janela"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatNumber(escopoItens)} item(ns) a remarcar neste
                      recorte
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="ml-auto h-8"
                    disabled={isExporting || total === 0}
                    onClick={() =>
                      exportAll({
                        storeCode,
                        labelType: filters.labelType,
                        batch: filters.batch,
                        status: effectiveStatus,
                        sortBy: filters.sortBy,
                        sortOrder: filters.sortOrder,
                      })
                    }
                  >
                    <Download className="mr-1 h-4 w-4" />
                    {isExporting ? "Solicitando..." : "Ordem de trabalho"}
                  </Button>
                </div>

                <div className="flex flex-wrap items-center gap-2 border-b bg-muted/30 px-4 py-2.5">
                  <Label className="text-xs text-gray-500">Situação:</Label>
                  {chips.map((chip) => (
                    <Button
                      key={chip.label}
                      type="button"
                      variant={
                        effectiveStatus === chip.value ? "default" : "outline"
                      }
                      size="sm"
                      className="h-7"
                      disabled={
                        chip.count === 0 && effectiveStatus !== chip.value
                      }
                      onClick={() => {
                        updateFilter({ key: "status", value: chip.value });
                        updateFilter({ key: "page", value: 1 });
                      }}
                    >
                      {chip.label}
                      <span className="ml-1.5 tabular-nums opacity-70">
                        {formatNumber(chip.count)}
                      </span>
                    </Button>
                  ))}
                </div>

                {isItemsError ? (
                  <div className="p-4">
                    <QueryErrorAlert
                      message={extractQueryErrorMessage(itemsError)}
                      onRetry={refetchItems}
                    />
                  </div>
                ) : (
                  <>
                    <div className="px-4 pt-3">
                      <MonitoringTable
                        columns={columns}
                        data={rows}
                        isLoading={isFetchingItems}
                        hidePagination
                        searchPlaceholder="Buscar SKU/descrição (nesta página)..."
                        emptyMessage={emptyMessage}
                        sorting={sorting}
                        onSortingChange={handleSortingChange}
                      />
                      <div className="py-2">
                        <ServerPagination
                          page={filters.page}
                          perPage={filters.perPage}
                          total={total}
                          onPageChange={(page) =>
                            updateFilter({ key: "page", value: page })
                          }
                          onPerPageChange={(perPage) => {
                            updateFilter({ key: "perPage", value: perPage });
                            updateFilter({ key: "page", value: 1 });
                          }}
                        />
                      </div>
                    </div>
                    <p className="border-t bg-muted/30 px-4 py-3 text-xs text-gray-500">
                      O <strong>preço de</strong> é o preço normal vigente{" "}
                      <strong>hoje</strong>, não o congelado no dia da
                      remarcação. Item sem preço normal vigente para comparar não
                      entra na lista — nem aqui, nem no coletor.
                    </p>
                  </>
                )}
              </Card>
            </>
          )}
        </div>
      </ContentContainer>
    </PageLayout>
  );
}

export default PriceRemarkPendingStore;
